import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type {
	CreateFolderRequest,
	Folder,
	FolderPaginationParams,
	PaginatedResponse,
	UpdateFolderRequest,
} from '../types/api';

/**
 * Query keys for folders caching
 */
export const folderKeys = {
	all: ['folders'] as const,
	lists: () => [...folderKeys.all, 'list'] as const,
	list: (params: FolderPaginationParams) => [...folderKeys.lists(), { params }] as const,
	detail: (id: string) => [...folderKeys.all, 'detail', id] as const,
};

/**
 * Hook to fetch all folders with caching
 */
export function useFolders() {
	return useQuery({
		queryKey: folderKeys.lists(),
		queryFn: async () => {
			const response = await api.folders.getFolders();
			// Handle both paginated and legacy array responses
			return Array.isArray(response) ? response : response.data;
		},
		staleTime: 10 * 60 * 1000, // 10 minutes (folders change less frequently)
		gcTime: 30 * 60 * 1000, // 30 minutes
	});
}

/**
 * Hook to fetch paginated folders with React Query
 */
export function useFoldersPaginated(params: FolderPaginationParams = {}) {
	return useQuery({
		queryKey: folderKeys.list(params),
		queryFn: async () => {
			const response = await api.folders.getFolders(params);
			// Handle both paginated and legacy array responses
			if (Array.isArray(response)) {
				const page = params.page || 1;
				const limit = params.limit || 20;
				const startIndex = (page - 1) * limit;
				const endIndex = startIndex + limit;
				const paginatedFolders = response.slice(startIndex, endIndex);

				return {
					data: paginatedFolders,
					pagination: {
						page,
						limit,
						total: response.length,
						pages: Math.ceil(response.length / limit),
						hasPrevious: page > 1,
						hasNext: endIndex < response.length,
					},
				} as PaginatedResponse<Folder>;
			}
			return response as PaginatedResponse<Folder>;
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 15 * 60 * 1000, // 15 minutes
	});
}

/**
 * Hook to fetch a single folder
 */
export function useFolder(id: string) {
	return useQuery({
		queryKey: folderKeys.detail(id),
		queryFn: () => api.folders.getFolder(id),
		enabled: !!id,
		staleTime: 10 * 60 * 1000,
	});
}

/**
 * Hook to create a new folder
 */
export function useCreateFolder() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateFolderRequest) => api.folders.createFolder(data),
		onSuccess: newFolder => {
			// Add to the folders list cache
			queryClient.setQueryData<Folder[]>(folderKeys.lists(), old => {
				return old ? [...old, newFolder] : [newFolder];
			});

			// Add to detail cache
			queryClient.setQueryData(folderKeys.detail(newFolder.id), newFolder);

			// Only invalidate paginated queries, not all lists
			queryClient.invalidateQueries({
				queryKey: folderKeys.lists(),
				predicate: query => query.queryKey.length > 2, // Only paginated queries
			});
		},
	});
}

/**
 * Hook to update a folder
 */
export function useUpdateFolder() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateFolderRequest }) =>
			api.folders.updateFolder(id, data),
		onMutate: async ({ id, data }) => {
			await queryClient.cancelQueries({ queryKey: folderKeys.detail(id) });
			await queryClient.cancelQueries({ queryKey: folderKeys.lists() });

			const previousFolder = queryClient.getQueryData<Folder>(folderKeys.detail(id));

			// Optimistically update detail cache
			if (previousFolder) {
				const updatedFolder = {
					...previousFolder,
					...data,
					updatedAt: new Date().toISOString(),
				};
				queryClient.setQueryData(folderKeys.detail(id), updatedFolder);

				// Optimistically update list cache
				queryClient.setQueryData<Folder[]>(folderKeys.lists(), old => {
					return old?.map(folder => (folder.id === id ? updatedFolder : folder)) || [];
				});
			}

			return { previousFolder };
		},
		onError: (err, variables, context) => {
			if (context?.previousFolder) {
				queryClient.setQueryData(folderKeys.detail(variables.id), context.previousFolder);
			}
			// Only invalidate if optimistic update failed
			queryClient.invalidateQueries({
				queryKey: folderKeys.lists(),
				exact: false,
			});
		},
		onSettled: (data, error, variables) => {
			// Only invalidate specific queries to reduce re-renders
			if (error) {
				queryClient.invalidateQueries({ queryKey: folderKeys.detail(variables.id) });
				queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
			}
		},
	});
}

/**
 * Hook to delete a folder
 */
export function useDeleteFolder() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => api.folders.deleteFolder(id),
		onMutate: async id => {
			await queryClient.cancelQueries({ queryKey: folderKeys.lists() });

			// Remove from cache
			queryClient.removeQueries({ queryKey: folderKeys.detail(id) });

			// Optimistically update list
			queryClient.setQueryData<Folder[]>(folderKeys.lists(), old => {
				return old?.filter(folder => folder.id !== id) || [];
			});
		},
		onError: () => {
			// Only invalidate on error to restore correct state
			queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
		},
		onSettled: (data, error) => {
			// Only invalidate if there was an error
			if (error) {
				queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
			}
		},
	});
}
